import { addDoc, collection, deleteDoc, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logListenerError } from './db';

const connectionsCollection = () => collection(db, 'connections');

/**
 * Live connections involving a profile, from both directions (userA requested, userB
 * received). `onChange` gets [{ id, ...data }]. Pass `state` to filter (e.g. 'accepted').
 */
export function subscribeConnections(profileId, onChange, { state } = {}, onError = logListenerError('connections')) {
  const filters = (field) => [where(field, '==', profileId), ...(state ? [where('state', '==', state)] : [])];
  let asRequester = [];
  let asRecipient = [];
  const emit = () => onChange([...asRequester, ...asRecipient]);
  const map = (snapshot) => snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));

  const unsubA = onSnapshot(query(connectionsCollection(), ...filters('userAId')), (snapshot) => {
    asRequester = map(snapshot);
    emit();
  }, onError);
  const unsubB = onSnapshot(query(connectionsCollection(), ...filters('userBId')), (snapshot) => {
    asRecipient = map(snapshot);
    emit();
  }, onError);
  return () => {
    unsubA();
    unsubB();
  };
}

/** Live set of profile IDs the given profile is connected with (accepted only). */
export function subscribeConnectedIds(profileId, onChange) {
  return subscribeConnections(profileId, (connections) => {
    onChange(new Set(connections.map((connection) => (connection.userAId === profileId ? connection.userBId : connection.userAId))));
  }, { state: 'accepted' });
}

/** Request a connection from `fromId` (the signed-in user) to `toId`. Resolves the new ID. */
export async function requestConnection(fromId, toId) {
  const ref = await addDoc(connectionsCollection(), {
    userAId: fromId, userBId: toId, state: 'pending', requestedBy: fromId, createdAt: Date.now(),
  });
  return ref.id;
}

export function acceptConnection(id) {
  return updateDoc(doc(db, 'connections', id), { state: 'accepted' });
}

export function removeConnection(id) {
  return deleteDoc(doc(db, 'connections', id));
}
